import { Button, Modal } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { UserIdentitySummary } from '@/api';
import {
  type FlowStep,
  useConnectedAccountsFlow,
} from '@/components/ConnectedAccounts/useConnectedAccountsFlow';
import { EmptyText } from '@/components/Ui/EmptyText';
import { LedgerRow } from '@/components/Ui/LedgerRow';
import { Section, SectionBody } from '@/components/Ui/Section';
import { Tag } from '@/components/Ui/Tag';
import { useUndoToast } from '@/components/Ui/Toast';
import { useAccountIdentity } from '@/hooks/useAccountIdentity';
import { shortNpub } from '@/lib/npub';
import { useRustyState } from '@/state';
import shared from '../AccountScreens.module.css';

export function SignInMethods() {
  const { t } = useTranslation();
  const toast = useUndoToast();
  const api = useRustyState.getState().api;
  const accountEmail = useAccountIdentity().email;
  const flow = useConnectedAccountsFlow(api);

  const [identities, setIdentities] = useState<UserIdentitySummary[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nostrSign, setNostrSign] = useState<(() => Promise<FlowStep>) | null>(null);

  const refresh = useCallback(async () => {
    try {
      setIdentities(await useRustyState.getState().api.listIdentities());
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleStep = (step: FlowStep) => {
    switch (step.kind) {
      case 'email_pending':
        setInfo(t('security.methods.checkEmail'));
        return;
      case 'nostr_sign_required':
        setNostrSign(() => step.sign);
        return;
      case 'completed':
        toast(t('security.methods.updated'));
        void refresh();
        return;
      case 'error':
        setError(
          t(`connectedAccounts.error.${step.code}`, { defaultValue: t('security.methods.failed') })
        );
        return;
      default:
        return;
    }
  };

  const run = async (key: string, start: () => Promise<FlowStep>) => {
    if (busy) return;
    setBusy(key);
    setError(null);
    setInfo(null);
    try {
      handleStep(await start());
    } catch {
      setError(t('security.methods.failed'));
    } finally {
      setBusy(null);
    }
  };

  const signWithNostr = async () => {
    const sign = nostrSign;
    setNostrSign(null);
    if (sign) await run('nostr-sign', sign);
  };

  const syncFromSso = async () => {
    if (!api.auth) return;
    setBusy('sync');
    try {
      const base = window.location.hash.split('?')[0];
      const returnTo = `${window.location.origin}${window.location.pathname}${base}?refreshed=1`;
      window.location.href = await api.auth.initiateOidcRefresh(returnTo);
    } catch {
      setError(t('security.methods.failed'));
      setBusy(null);
    }
  };

  if (failed) {
    return (
      <Section title={t('security.methods.title')}>
        <SectionBody>
          <p className={shared.errorText}>{t('security.methods.loadFailed')}</p>
        </SectionBody>
      </Section>
    );
  }

  const list = identities ?? [];
  const email = list.find((i) => i.method === 'EmailMagicLink');
  const sso = list.find((i) => i.method === 'Oidc');
  const nostr = list.find((i) => i.method === 'Nostr');
  // The account email signs in without an identity row; the backend counts it the same way.
  const usable = list.filter((i) => i.id !== null || i.method === 'EmailMagicLink').length;
  const canUnlink = (i?: UserIdentitySummary) => !!i && i.id !== null && usable > 1;
  const ssoWho = sso?.display || accountEmail;

  const disconnect = (identity: UserIdentitySummary) =>
    modals.openConfirmModal({
      title: t('security.methods.disconnectTitle'),
      children: <p style={{ margin: 0 }}>{t('security.methods.disconnectBody')}</p>,
      labels: { confirm: t('security.methods.disconnectConfirm'), cancel: t('ui.cancel') },
      onConfirm: () =>
        void run(identity.id as string, () => flow.startUnlink(identity.id as string)),
    });

  return (
    <Section title={t('security.methods.title')} subtitle={t('security.methods.subtitle')}>
      {identities === null ? (
        <SectionBody>
          <EmptyText>{t('ui.loading')}</EmptyText>
        </SectionBody>
      ) : (
        <>
          <LedgerRow
            label={t('security.methods.email')}
            value={
              email?.display_key ? (
                <span className={shared.mono}>{email.display_key}</span>
              ) : (
                <span className={shared.sub}>{t('security.methods.emailNone')}</span>
              )
            }
            help={email ? t('security.methods.emailHelp') : t('security.methods.emailNoneHelp')}
            action={email ? <Tag variant="ok">{t('ui.on')}</Tag> : undefined}
          />
          <LedgerRow
            label={t('security.methods.sso')}
            value={
              sso ? (
                <span>
                  {ssoWho
                    ? t('security.methods.ssoConnected', { who: ssoWho })
                    : t('security.methods.connected')}
                </span>
              ) : (
                <span className={shared.sub}>{t('security.methods.notConnected')}</span>
              )
            }
            help={t('security.methods.ssoHelp')}
            action={
              sso ? (
                <>
                  <Button
                    variant="subtle"
                    size="xs"
                    onClick={syncFromSso}
                    loading={busy === 'sync'}
                  >
                    {t('security.methods.ssoSync')}
                  </Button>
                  {canUnlink(sso) && (
                    <Button
                      variant="subtle"
                      size="xs"
                      onClick={() => disconnect(sso)}
                      loading={busy === sso.id}
                    >
                      {t('security.methods.disconnect')}
                    </Button>
                  )}
                </>
              ) : (
                <Button
                  variant="default"
                  size="xs"
                  onClick={() => run('link', flow.startLink)}
                  loading={busy === 'link'}
                >
                  {t('security.methods.connect')}
                </Button>
              )
            }
          />
          <LedgerRow
            label={t('security.methods.nostr')}
            value={
              nostr ? (
                <span className={shared.mono}>
                  {nostr.display_key
                    ? shortNpub(nostr.display_key)
                    : t('security.methods.connected')}
                </span>
              ) : (
                <span className={shared.sub}>{t('security.methods.notConnected')}</span>
              )
            }
            help={nostr ? t('security.methods.nostrHelp') : t('security.methods.nostrNoneHelp')}
            action={
              canUnlink(nostr) && nostr ? (
                <Button
                  variant="subtle"
                  size="xs"
                  onClick={() => disconnect(nostr)}
                  loading={busy === nostr.id}
                >
                  {t('security.methods.disconnect')}
                </Button>
              ) : undefined
            }
          />
          {(info || error) && (
            <SectionBody>
              <p
                className={error ? shared.errorText : shared.sub}
                role="status"
                style={{ margin: 0 }}
              >
                {error ?? info}
              </p>
            </SectionBody>
          )}
        </>
      )}

      <Modal
        opened={nostrSign !== null}
        onClose={() => setNostrSign(null)}
        title={t('security.methods.nostrSignTitle')}
      >
        <div className={shared.stack} style={{ gap: 16 }}>
          <span className={shared.sub}>{t('security.methods.nostrSignBody')}</span>
          <Button onClick={signWithNostr}>{t('security.methods.nostrSignButton')}</Button>
        </div>
      </Modal>
    </Section>
  );
}
